// CSS
import "../../../styles/global.scss";

import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import FormInputWrapper from "../../form-input-wrapper/FormInputWrapper.jsx";

import ReactMarkdown from "react-markdown";

import {
    selectInvalidFormFields,
    selectPreventFormSubmissionList,
} from "../../redux/form/form.selectors";

import {
    uuidv4,
    collectFormResponses,
    generateFormSummary,
    getElementAbsoluteYPosition,
    integerToFriendly,
} from "../js/form.utils";

import { DateTime, Settings } from "luxon";

import { generateEstimate } from "../js/estimateCalculations.js";

import axios from "axios";

/**
 * Configure the Luxon time zone so that all
 * instances of DateTime use the "correct" local time.
 * Timezone from: https://www.iana.org/time-zones
 *
 * @source https://moment.github.io/luxon/#/zones?id=changing-the-default-zone
 */
Settings.defaultZone = "Australia/Queensland";

let totalFormSections = 0;

const App = ({
    configuration,
    invalidFormFields,
    preventFormSubmissionList,
}) => {
    const [populateFrom, setPopulateForm] = useState(false);
    const [showFormErrors, setShowFormErrors] = useState(false);
    const [triggerValidation, setTriggerValidation] = useState(null);
    const [formErrors, setFormErrors] = useState([]);
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
    const [currentFormStep, setCurrentFormStep] = useState(1);
    const [alertMessage, setAlertMessage] = useState(null);
    const [showFormQuestions, setShowFormQuestions] = useState(true);
    const [showSummaryPage, setShowSummaryPage] = useState(false);
    const [showThanksPage, setShowThanksPage] = useState(false);
    const [formQuestions, setFormQuestions] = useState([]);
    const [calculationValues, setCalculationValues] = useState(null);
    const [formIntroduction, setFormIntroduction] = useState("");
    const [formDisclaimer, setFormDisclaimer] = useState("");
    const [parsedFormResponses, setParsedFormResponses] = useState([]);
    const [calculatedEstimate, setCalculatedEstimate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Request the form content and parse the response.
     */
    useEffect(() => {
        if (isLoading) {
            // Request the form content data JSON
            axios
                .get(configuration.formContentJsonEndpoint)
                .then((response) => {
                    const formQuestionsJson = [];
                    const formIntroductionMarkup =
                        response.data.introductionContent;
                    let formDisclaimerMarkup = "";

                    // No data
                    if (response.data === "") {
                        setAlertMessage(
                            "A problem has occurred with the form. \n\n**Error: Unable to retrieve content.**",
                        );
                        return;
                    }

                    // Invalid content format
                    if (
                        !response.data.questions ||
                        response.data.questions.length === 0
                    ) {
                        setAlertMessage(
                            "A problem has occurred with the form. \n\n**Error: Invalid content provided.**",
                        );
                        return;
                    }

                    // Capture the form questions
                    formQuestionsJson.push(...response.data.questions);

                    /**
                     * Create a unique ID for each step, section and
                     * first-level input in the provided data.
                     *
                     * Note: We also add the "preventProgression" property
                     * to the first question (the acknowledgement).
                     *
                     * Note: This will NOT create IDs for each of the
                     * inputs that are provided in the "conditionalInputs"
                     * or "additionalInputs" form data. Those are assigned
                     * an ID in each respective component.
                     */
                    for (let i = 0, j = formQuestionsJson.length; i < j; ++i) {
                        // Add the step ID
                        formQuestionsJson[i].appId = uuidv4();

                        for (
                            let k = 0,
                                x = formQuestionsJson[i].stepContent.length;
                            k < x;
                            ++k
                        ) {
                            // Add the section ID
                            formQuestionsJson[i].stepContent[k].appId =
                                uuidv4();

                            // Create a unique ID for each form field
                            for (
                                let d = 0,
                                    w =
                                        formQuestionsJson[i].stepContent[k]
                                            .sectionContent.length;
                                d < w;
                                ++d
                            ) {
                                // Add the question ID
                                formQuestionsJson[i].stepContent[
                                    k
                                ].sectionContent[d].appId = uuidv4();

                                // Prevent submission for the acknowledgement checkbox
                                if (i === 0 && k === 0 && d === 0) {
                                    formQuestionsJson[i].stepContent[
                                        k
                                    ].sectionContent[d].preventProgression = [
                                        "",
                                    ];
                                }
                            }
                        }
                    }

                    // Capture the total number of form sections
                    totalFormSections = formQuestionsJson.length;

                    // Replace the date stamp placeholder in the form disclaimer
                    if (response.data.summaryDisclaimer) {
                        formDisclaimerMarkup =
                            response.data.summaryDisclaimer.replace(
                                "[DATE_STAMP]",
                                DateTime.now().toFormat("dd/MM/yyyy"),
                            );
                    }

                    // Set the form disclaimer content in local state
                    setFormDisclaimer(formDisclaimerMarkup);

                    // Set the form introduction content in local state
                    setFormIntroduction(formIntroductionMarkup);

                    // Request the form calcuations JSON
                    axios
                        .get(configuration.formCalculationsJsonEndpoint)
                        .then((response) => {
                            // No data
                            if (response.data === "") {
                                setAlertMessage(
                                    "A problem has occurred with the form. \n\n**Error: Unable to retrieve calculations.**",
                                );
                                return;
                            }

                            // Invalid content format
                            if (!response.data.activityTimeUnitValues) {
                                setAlertMessage(
                                    "A problem has occurred with the form. \n\n**Error: Invalid calculations provided.**",
                                );
                                return;
                            }

                            // Set the calculation data in the local state
                            setCalculationValues(response.data);

                            // We have all the data we need, set the form content in local state
                            setFormQuestions(formQuestionsJson);
                        })
                        .catch((error) => {
                            // Catch any unhandled errors
                            setAlertMessage(
                                `A problem has occurred with the form. The calculations could not be retrieved. \n\n**Error: ${error.message}**`,
                            );
                        })
                        .finally(function () {
                            // Turn off the loading animation
                            setIsLoading(false);
                        });
                })
                .catch((error) => {
                    // Catch any unhandled errors
                    setAlertMessage(
                        `A problem has occurred with the form. The content could not be retrieved. \n\n**Error: ${error.message}**`,
                    );

                    // Turn off the loading animation
                    setIsLoading(false);
                });
        }
    }, [isLoading]);

    /**
     * When the invalid form fields are updated
     */
    useEffect(() => {
        let formStepErrors = [];

        // Check if the errors should be visible (e.g. user has clicked "Next")
        if (showFormErrors) {
            // Capture any errors for the current form step
            formStepErrors = getCurrentStepFieldErrors(
                invalidFormFields,
                currentFormStep,
            );

            if (formStepErrors.length !== 0) {
                // Add the error message(s) to the page
                setFormErrors(formStepErrors);
            } else {
                // Remove error message(s) from the page
                setFormErrors([]);
            }
        }
    }, [invalidFormFields, showFormErrors]);

    /**
     * When the form moves between steps
     */
    useEffect(() => {
        if (currentFormStep > 1 || showThanksPage || showSummaryPage) {
            // Move the page to the top of the form
            // Subtract 60px for the sticky mobile menu
            window.scrollTo({
                top: getElementAbsoluteYPosition("js-form-top") - 60,
                left: 0,
            });
        }
    }, [currentFormStep, showThanksPage, showSummaryPage]);

    const onPopulateButtonClick = (evt) => {
        evt.preventDefault();
        if (populateFrom) {
            setPopulateForm(false);
        } else {
            setPopulateForm(true);
        }
    };

    const onContinueButtonClick = (evt) => {
        // Check for any errors/missing fields on the current step
        const formStepErrors = getCurrentStepFieldErrors(
            invalidFormFields,
            currentFormStep,
        );

        evt.preventDefault();

        if (formStepErrors.length === 0) {
            // There are no form errors for the current step
            if (currentFormStep !== totalFormSections) {
                // Advance to the next form section
                setCurrentFormStep(currentFormStep + 1);

                // Hide form errors
                setShowFormErrors(false);
            }
        } else {
            // Display any form errors
            setShowFormErrors(true);

            // Trigger the form inputs to update their validation state (as necessary)
            setTriggerValidation({
                id: uuidv4(),
                formStep: currentFormStep,
            });
        }
    };

    const onBackButtonClick = (evt) => {
        evt.preventDefault();

        // Remove error message(s) from the page
        setFormErrors([]);

        // Hide current form errors
        setShowFormErrors(false);

        // Retreat to the previous form section
        setCurrentFormStep(currentFormStep - 1);
    };

    const onSummaryBackButtonClick = (evt) => {
        evt.preventDefault();

        // Re-enable the form submit button
        setSubmitButtonDisabled(false);

        // Remove the form summary from the local state
        setParsedFormResponses([]);

        // Remove the estimate from the local state
        setCalculatedEstimate(null);

        // Show the form
        setShowFormQuestions(true);

        // Hide the response summary
        setShowSummaryPage(false);
    };

    const onCalculationBackButtonClick = (evt) => {
        evt.preventDefault();

        // Show the response summary
        setShowSummaryPage(true);

        // Hide the calculation page
        setShowThanksPage(false);
    };

    const onShowCalculationButtonClick = (evt) => {
        evt.preventDefault();

        // Hide the response summary
        setShowSummaryPage(false);

        // Show the calculation page
        setShowThanksPage(true);
    };

    const onRestartFormButtonClick = (evt) => {
        evt.preventDefault();

        // Reset the auto-populate (for testing)
        setPopulateForm(false);

        /**
         * Reset all of the question responses by essentially
         * creating a whole new set of React elements.
         *
         * Create a unique ID for each step, section and
         * first-level input in the provided data.
         */
        const resetFormQuestions = [...formQuestions];

        for (let i = 0, j = resetFormQuestions.length; i < j; ++i) {
            // Add the step ID
            resetFormQuestions[i].appId = uuidv4();

            for (
                let k = 0, x = resetFormQuestions[i].stepContent.length;
                k < x;
                ++k
            ) {
                // Add the section ID
                resetFormQuestions[i].stepContent[k].appId = uuidv4();

                // Create a unique ID for each form field
                for (
                    let d = 0,
                        w =
                            resetFormQuestions[i].stepContent[k].sectionContent
                                .length;
                    d < w;
                    ++d
                ) {
                    // Add the question ID
                    resetFormQuestions[i].stepContent[k].sectionContent[
                        d
                    ].appId = uuidv4();

                    // Pre-populate the acknowledgement checkbox
                    if (i === 0 && k === 0 && d === 0) {
                        resetFormQuestions[i].stepContent[k].sectionContent[
                            d
                        ].inputDefaults = ["acknowledge"];
                    }
                }
            }
        }

        // Reset the form questions
        setFormQuestions(resetFormQuestions);

        // Reset the step count (to where the form questions start)
        setCurrentFormStep(2);

        // Re-enable the form submit button
        setSubmitButtonDisabled(false);

        // Remove the form summary from the local state
        setParsedFormResponses([]);

        // Remove the estimate from the local state
        setCalculatedEstimate(null);

        // Show the form
        setShowFormQuestions(true);

        // Hide the response summary
        setShowSummaryPage(false);

        // Hide the calculation page
        setShowThanksPage(false);
    };

    const onPrintButtonClick = (evt) => {
        evt.preventDefault();
        window.print();
    };

    const hijackFormSubmission = (evt) => {
        // Check for any errors/missing fields on the final step
        const formStepErrors = getCurrentStepFieldErrors(
            invalidFormFields,
            currentFormStep,
        );

        evt.preventDefault();

        if (formStepErrors.length === 0) {
            // Submit the form

            // Prevent multiple submissions
            setSubmitButtonDisabled(true);

            // Run the calculations
            generateSummaryAndEstimate();
        } else {
            // Display any form errors
            setShowFormErrors(true);

            // Trigger the form inputs to update their validation state (as necessary)
            setTriggerValidation({
                id: uuidv4(),
                formStep: currentFormStep,
            });
        }
    };

    /**
     * Convert the form data into the appropriate format,
     * generate the estimate and display the summary page.
     *
     * @returns void
     */
    function generateSummaryAndEstimate() {
        const form = document.getElementsByClassName(
            "js-custom-form-submission",
        )[0]; // Capture the form element
        const formFieldsArray = collectFormResponses(form);
        const formSummaryData = generateFormSummary(formFieldsArray);
        const calculatedEstimate = generateEstimate(
            formFieldsArray,
            calculationValues,
        );

        // Set the form summary in the local state
        setParsedFormResponses(formSummaryData);

        // Set the estimate in the local state
        setCalculatedEstimate(calculatedEstimate);

        // Hide the form
        setShowFormQuestions(false);

        // Show the response summary
        setShowSummaryPage(true);
    }

    /**
     * Return the list of current invalid fields for a given form step
     *
     * @param {Array} formErrors The list of form errors/required fields to filter
     * @param {Integer} formStep The current step in the form. e.g. 1, 2, 3
     *
     * @returns Array
     */
    function getCurrentStepFieldErrors(formErrors, formStep) {
        let currentFormStepErrors = [];

        // Check if any of the current step's inputs are invalid or required (and unanswered)
        if (formErrors.length !== 0) {
            // There are possibly form errors that need attention

            // Capture the errors relevant to the current form step
            currentFormStepErrors = formErrors.filter((errorObject) => {
                return errorObject.formStep === formStep;
            });
        }

        return currentFormStepErrors;
    }

    /**
     * Generate the markup for the summary table used on the
     * confirmation and calculation pages.
     *
     * @param {Array} formResponseData List of Objects representing the captured form data
     *
     * @returns React fragment
     */
    function generateSummaryTableContent(formResponseData) {
        return (
            <>
                <thead>
                    <tr>
                        <th
                            className="qld__table__header--width-75"
                            scope="col"
                            colSpan="2"
                        >
                            Question
                        </th>
                        <th scope="col">Response</th>
                    </tr>
                </thead>
                <tbody>
                    {formResponseData.map((responseData, allResponseIndex) => {
                        if (responseData.groupFields) {
                            // A response that's part of a group (form section)
                            return (
                                <React.Fragment
                                    key={`section_${responseData.sortPosition}`}
                                >
                                    {responseData.groupFields.map(
                                        (groupField, groupIndex) => {
                                            // Note: The first item in each groupField is the group heading

                                            const titleCellClassNames = [
                                                "cell-align-center table__cell--width-50",
                                            ];
                                            const dataCellClassNames = [
                                                "cell-align-center",
                                            ];

                                            // Add the bottom border to the last items in the group
                                            // Unless it's the last row in the table
                                            if (
                                                allResponseIndex + 1 !==
                                                formResponseData.length
                                            ) {
                                                titleCellClassNames.push(
                                                    "table__cell--bold-border",
                                                );
                                            }

                                            if (
                                                groupIndex + 1 ===
                                                    responseData.groupFields
                                                        .length &&
                                                allResponseIndex + 1 !==
                                                    formResponseData.length
                                            ) {
                                                dataCellClassNames.push(
                                                    "table__cell--bold-border",
                                                );
                                            }

                                            return (
                                                <tr key={groupField.fieldName}>
                                                    {groupIndex === 0 ? (
                                                        <td
                                                            className={titleCellClassNames.join(
                                                                " ",
                                                            )}
                                                            rowSpan={
                                                                responseData
                                                                    .groupFields
                                                                    .length
                                                            }
                                                        >
                                                            <span className="table__cell--character-wrap">
                                                                {
                                                                    responseData
                                                                        .groupFields[0]
                                                                        .sectionLabel
                                                                }
                                                            </span>
                                                        </td>
                                                    ) : null}
                                                    <td
                                                        className={dataCellClassNames.join(
                                                            " ",
                                                        )}
                                                    >
                                                        {
                                                            groupField.fieldValidationLabel
                                                        }
                                                    </td>
                                                    <td
                                                        className={dataCellClassNames.join(
                                                            " ",
                                                        )}
                                                    >
                                                        <span className="table__cell--character-wrap">
                                                            {
                                                                groupField.fieldValue
                                                            }
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        },
                                    )}
                                </React.Fragment>
                            );
                        } else {
                            // A single ungrouped response
                            return (
                                <tr key={responseData.fieldName}>
                                    <td
                                        colSpan="2"
                                        className="cell-align-center table__cell--bold-border"
                                    >
                                        <span className="table__cell--character-wrap">
                                            {responseData.fieldValidationLabel}
                                        </span>
                                    </td>
                                    <td className="cell-align-center table__cell--bold-border">
                                        <span className="table__cell--character-wrap">
                                            {responseData.fieldValue}
                                        </span>
                                    </td>
                                </tr>
                            );
                        }
                    })}
                </tbody>
            </>
        );
    }

    return (
        <div className="js-form-top custom-online-form__wrapper">
            {showFormQuestions && !isLoading ? (
                <>
                    {/* Form titles and step progress */}
                    {currentFormStep === 1 ? (
                        <h2 className="qld__display-xl">Introduction</h2>
                    ) : (
                        <>
                            <h2 className="qld__display-xl">Questions</h2>
                            <div
                                className={`form-progress__container ${totalFormSections > 1 ? "form-progress--visible" : ""}`}
                            >
                                <h2 className="sr-only">Form steps</h2>
                                {formQuestions.map((formSection, index) => {
                                    return index === currentFormStep - 1 ? (
                                        <h3
                                            key={formSection.appId}
                                            className="qld__display-lg custom-online-form__step-title"
                                        >
                                            {formSection.stepLabel}
                                        </h3>
                                    ) : null;
                                })}
                                {/* We don't count the first page as a step */}
                                {
                                    <p className="custom-online-form__step-counter">
                                        (Step {currentFormStep - 1} of{" "}
                                        {totalFormSections - 1})
                                    </p>
                                }
                            </div>
                        </>
                    )}
                </>
            ) : null}

            {alertMessage !== null ? (
                <section
                    className="qld__body qld__page-alerts--wrapper"
                    role="alert"
                    aria-live="polite"
                >
                    <div className="container-fluid">
                        <div className="qld__page-alerts qld__page-alerts--error">
                            <div className="qld__page-alerts--heading qld__display-lg">
                                Important
                            </div>
                            <div>
                                <ReactMarkdown>{alertMessage}</ReactMarkdown>
                            </div>
                        </div>
                    </div>
                </section>
            ) : null}

            <form
                action="/"
                method="post"
                className={`${!showFormQuestions ? "hide-form" : ""} js-custom-form-submission custom-online-form__form`}
            >
                {configuration.testingButton ? (
                    <button
                        onClick={onPopulateButtonClick}
                        className="populate-form__button qld__btn"
                        type="button"
                    >
                        Populate form
                        <br />
                        (TESTING ONLY)
                    </button>
                ) : null}

                {formQuestions.map((formStep, index) => {
                    const formStepNumber = index + 1;

                    return (
                        <section
                            key={formStep.appId}
                            className={
                                index === currentFormStep - 1
                                    ? "form-step__container is-current"
                                    : "form-step__container"
                            }
                        >
                            {/* Introduction content */}
                            {/* dangerouslySetInnerHTML should be fine to use if appropriate CSP headers are set for the page */}
                            {currentFormStep === 1 ? (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: formIntroduction,
                                    }}
                                />
                            ) : null}

                            {formStep.stepContent.map((formSection) => {
                                return (
                                    <fieldset
                                        key={formSection.appId}
                                        className={`custom-online-form__fieldset ${formSection.sectionClass || ""}`}
                                    >
                                        {formSection.sectionLabel ? (
                                            <legend className="qld__display-lg">
                                                {formSection.sectionLabel}
                                            </legend>
                                        ) : (
                                            <legend className="sr-only">
                                                Unlabelled group.
                                            </legend>
                                        )}
                                        {formSection.sectionDescription ? (
                                            <div className="custom-online-form__fieldset-description-wrapper">
                                                <p className="qld__hint-text">
                                                    {
                                                        formSection.sectionDescription
                                                    }
                                                </p>
                                            </div>
                                        ) : null}
                                        {formSection.sectionContent.map(
                                            (formInput) => {
                                                const formInputConfig = {
                                                    ...formInput,
                                                };

                                                /**
                                                 * If provided, add the section legend (label) to the input configuration.
                                                 * This is so we can include the section label in the form validation messages and summary.
                                                 */
                                                if (formSection.sectionLabel) {
                                                    formInputConfig.sectionLabel =
                                                        formSection.sectionLabel;
                                                }

                                                return (
                                                    <FormInputWrapper
                                                        key={formInput.appId}
                                                        inputConfiguration={
                                                            formInputConfig
                                                        }
                                                        autoPopulate={
                                                            populateFrom
                                                        }
                                                        triggerValidation={
                                                            triggerValidation
                                                        }
                                                        formStepValidation={
                                                            formStepNumber
                                                        }
                                                    />
                                                );
                                            },
                                        )}
                                    </fieldset>
                                );
                            })}
                        </section>
                    );
                })}

                <div className="custom-online-form__button-wrapper">
                    {currentFormStep !== 1 ? (
                        <button
                            type="button"
                            onClick={onBackButtonClick}
                            className="qld__btn"
                            disabled={
                                preventFormSubmissionList.length !== 0
                                    ? true
                                    : false
                            }
                        >
                            Back
                        </button>
                    ) : null}

                    {currentFormStep === totalFormSections ? (
                        <input
                            onClick={hijackFormSubmission}
                            disabled={
                                submitButtonDisabled ||
                                preventFormSubmissionList.length !== 0
                                    ? true
                                    : false
                            }
                            type="submit"
                            className="qld__btn"
                            value="Next"
                        />
                    ) : null}

                    {currentFormStep !== totalFormSections &&
                    formQuestions.length !== 0 ? (
                        <button
                            type="button"
                            onClick={onContinueButtonClick}
                            className="qld__btn"
                            disabled={
                                preventFormSubmissionList.length !== 0
                                    ? true
                                    : false
                            }
                        >
                            {currentFormStep === 1 ? "Start" : "Next"}
                        </button>
                    ) : null}

                    {submitButtonDisabled ? (
                        <div>
                            Please wait a moment while your responses are
                            recorded.
                        </div>
                    ) : null}
                </div>

                {formErrors.length !== 0 ? (
                    <div className="mt-3 qld__form-group qld__form-style-filled qld__form-group--invalid co-xs-12">
                        <span className="qld__label">Error</span>
                        <span className="qld__input--error">
                            Please check the following fields before continuing:
                        </span>
                        <ul>
                            {formErrors.map((field, index) => {
                                return (
                                    <li key={index}>
                                        {field.sectionLabel ? (
                                            <p className="qld__display-sm">
                                                {field.sectionLabel}
                                            </p>
                                        ) : null}
                                        <strong>{field.label}</strong>{" "}
                                        {field.required ? (
                                            <span>
                                                - This input is required.
                                            </span>
                                        ) : null}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ) : null}

                {isLoading ? (
                    <section className="qld__body">
                        <div className="container-fluid">
                            <div
                                className="qld__loading_spinner qld__loading_spinner--landscape"
                                role="status"
                            >
                                <span className="qld__loading_spinner-wheel"></span>
                                <div className="qld__loading_spinner-label">
                                    Retrieving form content&hellip;
                                </div>
                            </div>
                        </div>
                    </section>
                ) : null}
            </form>

            {showSummaryPage === true && parsedFormResponses.length !== 0 ? (
                <>
                    <h2 className="custom-online-form__summary-title qld__display-xl">
                        Summary
                    </h2>
                    <div className="qld__table--scroll">
                        <table className="qld__table">
                            {generateSummaryTableContent(parsedFormResponses)}
                        </table>
                    </div>

                    <div className="custom-online-form__button-wrapper">
                        <button
                            type="button"
                            onClick={onSummaryBackButtonClick}
                            className="qld__btn"
                        >
                            Back
                        </button>

                        <button
                            type="button"
                            onClick={onShowCalculationButtonClick}
                            className="qld__btn"
                        >
                            Submit
                        </button>
                    </div>
                </>
            ) : null}

            {showThanksPage === true && parsedFormResponses.length !== 0 ? (
                <>
                    <h2 className="custom-online-form__summary-title qld__display-xl">
                        Estimate page
                    </h2>
                    <h3 className="qld__display-lg custom-online-form__fee-title">
                        Estimated fee
                    </h3>
                    {calculatedEstimate !== null ? (
                        <>
                            <div
                                className={`${calculatedEstimate.timeUnitsWithinRange ? "qld__display-xl" : "qld__display-md"} custom-online-form__fee-estimate`}
                            >
                                <ReactMarkdown
                                    components={{
                                        p: ({ children, ...props }) => (
                                            <span {...props}>{children}</span>
                                        ),
                                    }}
                                >
                                    {calculatedEstimate.estimatedRange}
                                </ReactMarkdown>
                                {calculatedEstimate.timeUnitsWithinRange ? (
                                    <>
                                        {" "}
                                        <span className="qld__display-sm">
                                            (including GST)
                                        </span>
                                    </>
                                ) : null}
                            </div>
                            <p className="custom-online-form__fee-units">
                                Approximate standard units of effort:{" "}
                                {integerToFriendly(
                                    calculatedEstimate.totalTimeUnits,
                                )}
                            </p>
                        </>
                    ) : null}

                    <div className="custom-online-form__utility-button-wrapper">
                        <button
                            type="button"
                            onClick={onPrintButtonClick}
                            className="qld__btn"
                        >
                            Print
                        </button>
                    </div>

                    <div className="qld__table--scroll">
                        <table className="qld__table">
                            <caption>
                                How was this estimate calculated?
                                <br />
                                <span className="qld__caption">
                                    This estimate is based on the following
                                    estate details.
                                </span>
                            </caption>
                            {generateSummaryTableContent(parsedFormResponses)}
                        </table>
                    </div>

                    {/* dangerouslySetInnerHTML should be fine to use if appropriate CSP headers are set for the page */}
                    <div
                        dangerouslySetInnerHTML={{
                            __html: formDisclaimer,
                        }}
                    />

                    <div className="custom-online-form__button-wrapper">
                        <button
                            type="button"
                            onClick={onCalculationBackButtonClick}
                            className="qld__btn"
                        >
                            Back
                        </button>

                        <button
                            type="button"
                            onClick={onRestartFormButtonClick}
                            className="qld__btn sq-form-reset"
                        >
                            Restart
                        </button>
                    </div>
                </>
            ) : null}
        </div>
    );
};

App.propTypes = {
    configuration: PropTypes.object.isRequired,
    invalidFormFields: PropTypes.array,
    preventFormSubmissionList: PropTypes.array,
};

const mapStateToProps = (state) => {
    return {
        invalidFormFields: selectInvalidFormFields(state),
        preventFormSubmissionList: selectPreventFormSubmissionList(state),
    };
};

export default connect(mapStateToProps)(App);
